/**
 * The count of one size, editable on its own.
 *
 * Deliberately separate from the price form. Stock is the field a vendor touches
 * daily — often on a phone, mid-shift — and making them open a form that also holds
 * price and MRP invites a stale form re-posting an old price alongside the new count.
 * One number, one save.
 *
 * Not tracking is a first-class choice, not a missing value. A vendor who cannot keep
 * a count honest is better off without one: a stale number takes their listing off the
 * shelf, which is worse for them than a listing that never claims to know.
 */
import { useState } from 'react';
import { Check, Loader2, PackageX, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { form_error_message } from '@/lib/forms';
import { cn } from '@/lib/utils';

export type VariantStock = {
  is_in_stock: boolean;
  stock_qty?: number | null;
  is_low_stock?: boolean;
  is_stock_tracked?: boolean;
};

export function VariantStockControl({
  variant_id,
  stock,
  on_save,
  className,
}: {
  variant_id: string;
  stock: VariantStock;
  /** Null stops tracking. Returns how many waiting shoppers were told, if any. */
  on_save: (stock_qty: number | null) => Promise<{ watchers_notified?: number } | void>;
  className?: string;
}) {
  const tracked = stock.is_stock_tracked ?? stock.stock_qty != null;
  const [editing, set_editing] = useState(false);
  const [value, set_value] = useState(tracked ? String(stock.stock_qty ?? 0) : '');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [told, set_told] = useState<number | null>(null);

  async function save(next: number | null) {
    set_error(null);
    set_told(null);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      return set_error('Enter a whole number, 0 or more.');
    }
    set_busy(true);
    try {
      const res = await on_save(next);
      set_editing(false);
      if (res && typeof res.watchers_notified === 'number' && res.watchers_notified > 0) {
        set_told(res.watchers_notified);
      }
    } catch (err) {
      set_error(form_error_message(err, 'Could not update stock.'));
    } finally {
      set_busy(false);
    }
  }

  return (
    <div className={cn('flex flex-col gap-1', className)} data-testid={`stock-control-${variant_id}`}>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min={0}
            step={1}
            value={value}
            onChange={(e) => set_value(e.target.value)}
            className="h-9 w-20"
            aria-label="Units in stock"
            data-testid={`stock-input-${variant_id}`}
          />
          <Button
            size="sm"
            variant="gradient"
            disabled={busy}
            onClick={() => save(value.trim() === '' ? null : Number(value))}
            data-testid={`stock-save-${variant_id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => set_editing(false)} disabled={busy}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {!tracked ? (
            <>
              <span className="text-xs text-muted-foreground">Stock not tracked</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  set_value('');
                  set_editing(true);
                }}
                data-testid={`stock-start-${variant_id}`}
              >
                Track stock
              </Button>
            </>
          ) : (
            <>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  !stock.is_in_stock
                    ? 'bg-destructive/12 text-destructive'
                    : stock.is_low_stock
                      ? 'bg-warning/15 text-warning'
                      : 'bg-success/12 text-success',
                )}
                data-testid={`stock-badge-${variant_id}`}
              >
                {stock.is_in_stock ? `${stock.stock_qty} in stock` : 'Out of stock'}
              </span>
              {!stock.is_in_stock && (
                /* The consequence, stated. A vendor should not have to infer it. */
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <PackageX className="h-3 w-3" /> customers cannot buy this size
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  set_value(String(stock.stock_qty ?? 0));
                  set_editing(true);
                }}
                data-testid={`stock-edit-${variant_id}`}
              >
                Update
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => save(null)}
                disabled={busy}
                data-testid={`stock-untrack-${variant_id}`}
              >
                Stop tracking
              </Button>
            </>
          )}
        </div>
      )}

      {told !== null && (
        /*
         * Restocking is the moment the watch list pays off, and the vendor should see
         * that it did — it is demand they can act on next time.
         */
        <p className="flex items-center gap-1 text-[11px] font-medium text-success" data-testid={`stock-notified-${variant_id}`}>
          <Bell className="h-3 w-3" />
          {told} {told === 1 ? 'customer was' : 'customers were'} waiting and {told === 1 ? 'has' : 'have'} been told.
        </p>
      )}
      {error && (
        <p className="text-[11px] text-destructive" role="alert" data-testid={`stock-error-${variant_id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
